/* eslint-disable no-unused-vars */
import React, { useMemo, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../components/Table/Table";
import { Header } from "../../../components";
import { ApproverPendingBaseColumns } from "../../../utils/utils";
import PhysicalDocumentToggle from "../../../components/TableComponents/PhysicalDocumentToggle/PhysicalDocumentToggle";
import TicketButton from "../../../components/TableComponents/TicketButton/TicketButton";
import TableLayout from "../../../layouts/TableLayout/TableLayout";
import ActionButtons from "../../../components/TableComponents/ActionButtons/ActionButtons";
import { ROUTES } from "../../../constants";
import useTicketData  from "../../../hooks/useTicketData";
import useTicketFilters from "../../../utils/ticketFilters";
import useTicketExport from "../../../hooks/useTicketExport";
import NoItemsFound from "../../../components/NoItemsFound/NoItemsFound";
import { VIEW_TYPE } from "../../../utils/ticketUtils";
import { API } from "../../../apis/api";
import { errorSnackbar } from "../../../redux/features/customSnackbar/action";


function ApproverPending() {
  const history = useHistory();
  const dispatch = useDispatch()
  const userId = useSelector(state=>state.auth.user.userId)

  const [selectedView, setSelectedView] = useState(VIEW_TYPE.OPEN);
  const viewOptions = [
    { label: "Open", value: VIEW_TYPE.OPEN },
    { label: "Assigned to Me", value: VIEW_TYPE.ASSIGNED },
  ];

  // Custom hooks
  const {
    searchQuery,
    allRows: filteredRows,
    loading,
    isRefreshing,
    handleRefresh,
    handleSearchQuery,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage,
  } = useTicketData("PERFORMER_APPROVED", true, selectedView); // enableSearch = true for approver

  const handleViewChange = useCallback((view) => {
    setSelectedView(view);
    setPage(0);
  }, []);

  const handleViewPhysicalDocument = useCallback(
    (ticketId, ticketCode) => {
      history.push(
        ROUTES.GOTOQUESTIONNAIRE(ticketId, ticketCode, "approver")
      );
    },
    [history]
  );

  const assignedTicketToUser = async (ticketNumber, userID) => {
    const payload = {
      sub: userID,
      ticket_code: ticketNumber,
    };
    try {
      const response = await API.post(
        "/ticket/assign-ticket-to-user/",
        payload
      );
      return response.data;
    } catch (error) {
      console.log("ticket not assign to user", error);
      throw error;
    }
  };

  const handleActionClick = useCallback(
    async (action, rowData) => {
      if (action === "Update EL") {
        console.log(`Updating EL for ticket: ${rowData.ticketNumber}`);
      }
      if (action === "View Details") {
        try {
          const res = await assignedTicketToUser(rowData.ticketNumber, userId);

          console.log("Ticket assigned successfully");
          handleViewPhysicalDocument(rowData.id, rowData.ticketNumber);
        } catch (error) {
          dispatch(
            errorSnackbar({
              title: "Failed to assign",
              subtitle:
                error?.response?.data?.error_message ||
                "Failed to assign the ticket. Please try again.",
              duration: 5000,
            })
          );
          console.error("Failed to assign ticket:", error);
        }
      }
    },
    [handleViewPhysicalDocument, userId]
  );

  // Column configuration
  const columns = useMemo(() => {
    const updatedColumns = [...ApproverPendingBaseColumns];

    updatedColumns[0] = {
      width: 180,
      title: "Ticket Number",
      field: "ticketNumber",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <TicketButton
          ticketNumber={rowData.ticketNumber}
          ticketId={rowData.id}
        />
      ),
    };

    updatedColumns[6] = {
      width: 220,
      title: "Action",
      field: "action",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <ActionButtons
          actions={["View Details"]} // Set default actions for approver
          status={rowData.status}
          onActionClick={(action) => handleActionClick(action, rowData)}
        />
      ),
    };

    updatedColumns[3] = {
      width: 260,
      title: "Physical Document Received",
      field: "physicalDocumentReceived",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => {
        const value = rowData.physicalDocumentReceived;
        return (
          <PhysicalDocumentToggle
            checked={value}
            disabled={rowData.ai_agent_status === "IN_PROGRESS" || value}
            onChange={(e) => console.log("")}
          />
        );
      },
    };

    return updatedColumns;
  }, [handleActionClick]);

  const { handleExport, isExporting } = useTicketExport(
    "PERFORMER_APPROVED",
    columns,
    selectedView
  );

  return (
    <TableLayout
      styles={{ tableInnerWrapper: { paddingBottom: "0rem" } }}
      header={
        <Header
          searchQuery={searchQuery}
          handleSearchQuery={handleSearchQuery}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onExport={handleExport}
          isExporting={isExporting}
          showViewToggle
          selectedView={selectedView}
          onViewChange={handleViewChange}
          viewOptions={viewOptions}
          disableExport={filteredRows.length === 0}
        />
      }
    >
      {filteredRows.length === 0 && !(loading) ? (
        <NoItemsFound />
      ) : (
        <Table
          columns={columns}
          defaultColumns={columns}
          rows={filteredRows}
          hideCheckbox
          rowHeight={45}
          setHasActiveEdits={() => {}}
          isDataLoading={loading || isRefreshing}
          serverSide
          totalRows={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </TableLayout>
  );
}

export default ApproverPending;
